/**
 * PaymentsActivationCard - State-driven card for payments activation
 * 
 * Single source of truth: GET /api/settings/payments/activation-status
 * 
 * States:
 * - NOT_STARTED: "Start application" CTA -> onboarding-link
 * - IN_PROGRESS: "Continue application" CTA -> onboarding-link (idempotent)
 * - APPROVED_UNPAID: "Activate online payments" CTA -> subscription purchase
 * - ACTIVE: Show success state, link to billing
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  ArrowRight, 
  SpinnerGap,
  Info,
  Receipt,
  ShieldCheck,
  Buildings,
  IdentificationCard,
  ArrowSquareOut,
  Warning,
  Lightning
} from "phosphor-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Status pill component
 */
function StatusPill({ state }) {
  const config = {
    NOT_STARTED: {
      bg: "bg-zinc-100",
      text: "text-zinc-600",
      border: "border-zinc-300",
      label: "Not started",
    },
    IN_PROGRESS: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-300",
      label: "In progress",
    },
    APPROVED_UNPAID: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-300",
      label: "Approved",
    },
    ACTIVE: {
      bg: "bg-emerald-100",
      text: "text-emerald-800",
      border: "border-emerald-400",
      label: "Active",
    },
  };

  const c = config[state] || config.NOT_STARTED;

  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
      data-testid="status-pill"
      data-state={state}
    >
      {state === "ACTIVE" && <CheckCircle size={12} weight="fill" />}
      {state === "IN_PROGRESS" && <Clock size={12} weight="fill" />}
      {c.label}
    </span>
  );
}

/**
 * Loading skeleton for the card
 */
function LoadingSkeleton() {
  return (
    <Card className="border-2 border-zinc-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-6 w-6 bg-zinc-200 rounded animate-pulse" />
            <div className="h-6 w-48 bg-zinc-200 rounded animate-pulse" />
          </div>
          <div className="h-6 w-24 bg-zinc-200 rounded-full animate-pulse" />
        </div>
        <div className="h-4 w-64 bg-zinc-100 rounded animate-pulse mt-2" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-20 bg-zinc-100 rounded animate-pulse" />
        <div className="h-10 bg-zinc-200 rounded animate-pulse" />
      </CardContent>
    </Card>
  );
}

/**
 * Error state with retry
 */
function ErrorState({ message, onRetry }) {
  return (
    <Card className="border-2 border-red-200 bg-red-50">
      <CardHeader>
        <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-red-800">
          <Warning size={20} />
          Payment Setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-red-700">{message}</p>
        <Button 
          onClick={onRetry} 
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-100"
          data-testid="retry-btn"
        >
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

export default function PaymentsActivationCard({ tenant, onRefresh }) {
  const navigate = useNavigate();
  const [activationStatus, setActivationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [error, setError] = useState(null);

  // =========================================================================
  // FETCH ACTIVATION STATUS
  // =========================================================================
  
  const fetchActivationStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/settings/payments/activation-status`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to load payment status");
      }
      
      const data = await response.json();
      setActivationStatus(data);
    } catch (err) {
      console.error("[PaymentsActivationCard] Fetch error:", err);
      setError(err.message || "Failed to load payment status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivationStatus();
  }, [fetchActivationStatus]);

  // =========================================================================
  // CTA HANDLERS
  // =========================================================================
  
  /**
   * Handle Start/Continue application
   * Calls POST /api/payments/finix/onboarding-link (idempotent)
   * Opens onboarding_url in new tab
   */
  const handleOnboardingCTA = async () => {
    setCtaLoading(true);
    
    try {
      const response = await fetch(`${API}/payments/finix/onboarding-link`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      
      // Check content-type to avoid parsing non-JSON responses
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const statusText = response.status === 521 ? "Server down" : 
                          response.status === 502 ? "Bad gateway" :
                          response.status === 503 ? "Service unavailable" :
                          `HTTP ${response.status}`;
        throw new Error(`Unable to connect to payment service (${statusText})`);
      }
      
      const data = await response.json();
      
      if (data.onboarding_url) {
        window.open(data.onboarding_url, "_blank", "noopener,noreferrer");
        toast.success("Payment setup opened in new tab");
        
        // Refetch status
        await fetchActivationStatus();
        if (onRefresh) onRefresh();
      } else if (data.status === "approved") {
        toast.success("Already approved! Refresh to see updated status.");
        await fetchActivationStatus();
      } else if (data.ok === false) {
        // Backend returned structured error
        throw new Error(data.message || "Failed to get onboarding link");
      } else {
        throw new Error("Failed to get onboarding link");
      }
    } catch (err) {
      console.error("[PaymentsActivationCard] Onboarding error:", err);
      toast.error(err.message || "Failed to start setup. Please try again.");
    } finally {
      setCtaLoading(false);
    }
  };

  /**
   * Handle Activate (subscription purchase)
   * Navigates to upgrades page or triggers subscription flow
   */
  const handleActivateSubscription = () => {
    // Navigate to upgrades page where subscription can be purchased
    navigate("/settings/upgrades?focus=payment_processing");
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={fetchActivationStatus} />;
  }

  const state = activationStatus?.state || "NOT_STARTED";

  // =========================================================================
  // STATE: NOT_STARTED
  // Conversion-optimized layout: Benefits → Pricing → CTA → Requirements
  // =========================================================================
  if (state === "NOT_STARTED") {
    return (
      <Card className="border-2 border-zinc-200" data-testid="payments-activation-card" data-state={state}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <CreditCard size={20} className="text-zinc-600" />
              Accept Online Payments
            </CardTitle>
            <StatusPill state={state} />
          </div>
          {/* Section 1: Description */}
          <CardDescription>
            Start accepting credit card and bank payments directly from your estimates and invoices.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Section 2: High-Value Conversion Statement */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 text-center">
            <p className="text-zinc-800 text-sm sm:text-base">
              Contractors using online payments typically get paid{" "}
              <span className="font-heading font-bold text-[#FF6600] uppercase">3–5× faster</span>{" "}
              than paper invoices.
            </p>
          </div>

          {/* Section 2b: Approval Reassurance Badge */}
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <span className="text-lg">⚡</span>
            <span className="font-medium">5-minute application</span>
            <span className="text-emerald-600">•</span>
            <span>Fast approval process</span>
          </div>

          {/* Section 3: Benefits */}
          <div className="bg-zinc-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-zinc-700 mb-3">Why Contractors Love It</h4>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>Customers can pay invoices instantly online</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>Collect deposits when sending estimates</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>Accept cards over the phone using the built-in Virtual Terminal</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>Funds deposited to your bank in ~2 business days</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>No hardware required</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>No monthly fees</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>No hard credit inquiry</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                <span>Quick 5-minute application</span>
              </li>
            </ul>
          </div>

          {/* Section 4: Pricing */}
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-zinc-700 mb-2">Simple Pricing</h4>
            <p className="text-zinc-900">
              <span className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-tight">2.99% + $0.30</span>
              <span className="text-sm font-normal text-zinc-500 ml-2">per transaction</span>
            </p>
            <p className="text-sm text-zinc-500 mt-1">No monthly fees. No contracts.</p>
          </div>

          {/* Section 5: CTA */}
          <div className="space-y-2">
            <Button
              onClick={handleOnboardingCTA}
              disabled={ctaLoading}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#E65C00] text-white text-base gap-2"
              data-testid="start-application-btn"
            >
              {ctaLoading ? (
                <SpinnerGap className="animate-spin" size={20} />
              ) : (
                <>
                  Start Accepting Payments
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </Button>
            <p className="text-xs text-zinc-500 text-center">
              No hard credit check • No monthly fees
            </p>
          </div>

          {/* Section 6: Requirements (moved below CTA) */}
          <div className="bg-zinc-50 rounded-lg p-4 border border-zinc-100">
            <h4 className="text-sm font-medium text-zinc-600 mb-3">What you'll need</h4>
            <ul className="space-y-2 text-sm text-zinc-500">
              <li className="flex items-start gap-2">
                <IdentificationCard size={18} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                <span>Business or personal identification</span>
              </li>
              <li className="flex items-start gap-2">
                <Buildings size={18} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                <span>Business address and contact information</span>
              </li>
              <li className="flex items-start gap-2">
                <Receipt size={18} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                <span>Bank account for deposits (routing & account number)</span>
              </li>
            </ul>
          </div>

          {/* Section 7: Footer */}
          <p className="text-xs text-zinc-400 text-center">
            Takes about 5 minutes • Secure application via Finix
          </p>
        </CardContent>
      </Card>
    );
  }

  // =========================================================================
  // STATE: IN_PROGRESS
  // =========================================================================
  if (state === "IN_PROGRESS") {
    return (
      <Card className="border-2 border-amber-200 bg-amber-50/30" data-testid="payments-activation-card" data-state={state}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Clock size={20} className="text-amber-600" />
              Finish Your Application
            </CardTitle>
            <StatusPill state={state} />
          </div>
          <CardDescription>
            Complete your payment setup to start accepting deposits and card payments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Reassurance */}
          <div className="bg-white/70 rounded-lg p-4 border border-amber-200">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-zinc-700">You're almost done</p>
                <p className="text-sm text-zinc-600 mt-1">
                  Your application is saved. Continue where you left off — it takes about 2 minutes to complete.
                </p>
              </div>
            </div>
          </div>

          {/* Motivation Callout */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/70 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3 justify-center">
              <Lightning size={18} weight="fill" className="text-amber-500 flex-shrink-0" />
              <p className="text-sm text-zinc-700">
                Contractors using online payments typically get paid{" "}
                <span className="font-semibold text-amber-700">3–5× faster</span>{" "}
                than paper invoices.
              </p>
            </div>
          </div>

          {/* Application details if available */}
          {activationStatus?.finix?.onboarding_form_id && (
            <div className="text-xs text-zinc-500 flex items-center gap-2">
              <span>Application ID:</span>
              <code className="bg-white px-2 py-0.5 rounded border border-zinc-200">
                {activationStatus.finix.onboarding_form_id.substring(0, 16)}...
              </code>
            </div>
          )}

          {/* CTA */}
          <Button
            onClick={handleOnboardingCTA}
            disabled={ctaLoading}
            className="w-full h-12 bg-[#FF6600] hover:bg-[#E65C00] text-white text-base gap-2"
            data-testid="continue-application-btn"
          >
            {ctaLoading ? (
              <SpinnerGap className="animate-spin" size={20} />
            ) : (
              <>
                Continue application
                <ArrowSquareOut size={18} weight="bold" />
              </>
            )}
          </Button>

          <p className="text-xs text-zinc-400 text-center">
            Opens in a new tab • ~2 minutes to complete
          </p>
        </CardContent>
      </Card>
    );
  }

  // =========================================================================
  // STATE: APPROVED_UNPAID
  // =========================================================================
  if (state === "APPROVED_UNPAID") {
    return (
      <Card className="border-2 border-emerald-200 bg-emerald-50/30" data-testid="payments-activation-card" data-state={state}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              You're Approved 🎉
            </CardTitle>
            <StatusPill state={state} />
          </div>
          <CardDescription>
            One more step to start accepting payments from your customers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Info box */}
          <div className="bg-white/70 rounded-lg p-4 border border-emerald-200">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-zinc-700">Your application was approved</p>
                <p className="text-sm text-zinc-600 mt-1">
                  Activate your subscription to unlock online deposits and invoice payments.
                </p>
              </div>
            </div>
          </div>

          {/* What's included */}
          <div className="bg-zinc-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-zinc-700 mb-2">What you'll get</h4>
            <ul className="space-y-1.5 text-sm text-zinc-600">
              <li className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-500" />
                Accept deposits on estimates
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-500" />
                Send payment links with invoices
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-500" />
                Get paid faster with card payments
              </li>
            </ul>
          </div>

          {/* CTA */}
          <Button
            onClick={handleActivateSubscription}
            disabled={ctaLoading}
            className="w-full h-12 bg-[#FF6600] hover:bg-[#E65C00] text-white text-base gap-2"
            data-testid="activate-subscription-btn"
          >
            {ctaLoading ? (
              <SpinnerGap className="animate-spin" size={20} />
            ) : (
              <>
                Activate online payments
                <ArrowRight size={18} weight="bold" />
              </>
            )}
          </Button>

          <p className="text-xs text-zinc-400 text-center">
            Subscription required to process payments
          </p>
        </CardContent>
      </Card>
    );
  }

  // =========================================================================
  // STATE: ACTIVE
  // =========================================================================
  return (
    <Card className="border-2 border-emerald-300 bg-emerald-50/50" data-testid="payments-activation-card" data-state={state}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <CheckCircle size={20} weight="fill" className="text-emerald-600" />
            Payments Are Active
          </CardTitle>
          <StatusPill state={state} />
        </div>
        <CardDescription>
          You're all set to accept online payments from your customers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* What's enabled */}
        <div className="bg-white/70 rounded-lg p-4 border border-emerald-200">
          <h4 className="text-sm font-medium text-zinc-700 mb-3">What's enabled</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              Deposit collection
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              Invoice payments
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              Card payments
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <CheckCircle size={16} className="text-emerald-500" weight="fill" />
              Payment links
            </div>
          </div>
        </div>

        {/* Merchant info */}
        {activationStatus?.finix?.application_id && (
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <span>Merchant ID:</span>
            <code className="bg-white px-2 py-0.5 rounded border border-zinc-200">
              {activationStatus.finix.application_id.substring(0, 16)}...
            </code>
          </div>
        )}

        {/* Links */}
        <div className="pt-2 border-t border-emerald-200 flex gap-4">
          <Button
            onClick={() => navigate("/settings/upgrades")}
            variant="ghost"
            size="sm"
            className="text-zinc-600 hover:text-zinc-900"
            data-testid="view-billing-btn"
          >
            <Receipt size={16} className="mr-1.5" />
            View billing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
